import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BulkBatchDto,
  BulkOperation,
  BulkRowDto,
  ChangeReasonDto,
  ChangeReasonInput,
  InventoryFullDashboard,
  InventoryEventDto,
  InventorySummary,
  Paged,
  ProductModelDto,
  ProductModelInput,
  UnitCreateInput,
  UnitDto,
  UnitUpdateInput,
} from '@app/shared';
import { api } from '../../lib/api';
import type { Query } from '../../lib/api';

/* ---------------------------------- Units --------------------------------- */

export const useInventorySummary = () =>
  useQuery({
    queryKey: ['inventory', 'summary'],
    queryFn: () => api.get<InventorySummary>('/inventory/summary'),
  });

/** "SIM & Device Add/Update Activity" and "Inventory Health" (Full Inventory Dashboard). */
export const useInventoryActivity = () =>
  useQuery({
    queryKey: ['inventory', 'activity'],
    queryFn: () => api.get<InventoryFullDashboard>('/inventory/activity'),
  });

export const useUnits = (params: Query, enabled = true) =>
  useQuery({
    queryKey: ['inventory', 'units', params],
    queryFn: () => api.get<Paged<UnitDto>>('/inventory/units', params),
    placeholderData: (prev) => prev,
    enabled,
  });

export interface UnitDetail {
  unit: UnitDto;
  events: InventoryEventDto[];
}

export const useUnit = (id: string | undefined) =>
  useQuery({
    queryKey: ['inventory', 'unit', id],
    queryFn: () => api.get<UnitDetail>(`/inventory/units/${id}`),
    enabled: Boolean(id),
    // The edit form is built once from this answer, so never start it from a cached copy.
    staleTime: 0,
    gcTime: 0,
  });

/** Link for the browser to download the filtered list as CSV. */
export const unitExportUrl = (filters: Query) => api.url('/inventory/units/export', filters);

/** Unit changes move the dashboard numbers and the per-model unit counts. */
function useUnitMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['inventory'] }),
        queryClient.invalidateQueries({ queryKey: ['product-models'] }),
      ]),
  });
}

export const useAddUnit = () =>
  useUnitMutation((input: UnitCreateInput) =>
    api.post<{ data: UnitDto[] }>('/inventory/units', input),
  );
export const useUpdateUnit = () =>
  useUnitMutation(({ id, input }: { id: string; input: UnitUpdateInput }) =>
    api.patch<UnitDto>(`/inventory/units/${id}`, input),
  );

/* ------------------------------ Product models ----------------------------- */

export interface ProductModelOption {
  id: string;
  make: string;
  model: string;
  sku: string;
  isSim: boolean;
  amountCents?: number;
}

export const useProductModels = (params: Query) =>
  useQuery({
    queryKey: ['product-models', 'list', params],
    queryFn: () => api.get<Paged<ProductModelDto>>('/product-models', params),
    placeholderData: (prev) => prev,
  });

export const useProductModelOptions = (enabled = true) =>
  useQuery({
    queryKey: ['product-models', 'options'],
    queryFn: () => api.get<{ data: ProductModelOption[] }>('/product-models/options'),
    select: (r) => r.data,
    enabled,
  });

function useModelMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['product-models'] }),
  });
}

export const useCreateProductModel = () =>
  useModelMutation((input: ProductModelInput) =>
    api.post<ProductModelDto>('/product-models', input),
  );
export const useUpdateProductModel = () =>
  useModelMutation(({ id, input }: { id: string; input: Partial<ProductModelInput> }) =>
    api.patch<ProductModelDto>(`/product-models/${id}`, input),
  );

/* ------------------------------ Change reasons ----------------------------- */

export type ChangeReasonOption = Pick<
  ChangeReasonDto,
  'id' | 'name' | 'reasonStatus' | 'processType'
>;

export const useChangeReasons = (params: Query) =>
  useQuery({
    queryKey: ['change-reasons', 'list', params],
    queryFn: () => api.get<Paged<ChangeReasonDto>>('/change-reasons', params),
    placeholderData: (prev) => prev,
  });

export const useChangeReasonOptions = (enabled = true) =>
  useQuery({
    queryKey: ['change-reasons', 'options'],
    queryFn: () => api.get<{ data: ChangeReasonOption[] }>('/change-reasons/options'),
    select: (r) => r.data,
    enabled,
  });

function useReasonMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['change-reasons'] }),
  });
}

export const useCreateChangeReason = () =>
  useReasonMutation((input: ChangeReasonInput) =>
    api.post<ChangeReasonDto>('/change-reasons', input),
  );
export const useUpdateChangeReason = () =>
  useReasonMutation(({ id, input }: { id: string; input: Partial<ChangeReasonInput> }) =>
    api.patch<ChangeReasonDto>(`/change-reasons/${id}`, input),
  );

/* ------------------------------- Bulk batches ------------------------------ */

export const useBulkBatches = (operation: BulkOperation, params: Query) =>
  useQuery({
    queryKey: ['bulk', 'batches', operation, params],
    queryFn: () => api.get<Paged<BulkBatchDto>>('/bulk/batches', { ...params, operation }),
    placeholderData: (prev) => prev,
  });

export const useBulkRows = (batchId: string | undefined, params: Query) =>
  useQuery({
    queryKey: ['bulk', 'rows', batchId, params],
    queryFn: () => api.get<Paged<BulkRowDto>>(`/bulk/batches/${batchId}/rows`, params),
    placeholderData: (prev) => prev,
    enabled: Boolean(batchId),
  });

export type BulkSubmit =
  | { type: 'paste'; identifiers: string[]; params: Record<string, unknown> }
  | { type: 'file'; file: File; params: Record<string, unknown> };

/** Submits "Type - 1" (pasted values) or "Type - 2" (CSV file) and returns the finished batch. */
export function useBulkSubmit(operation: BulkOperation) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkSubmit) => {
      if (input.type === 'paste')
        return api.post<BulkBatchDto>(`/bulk/${operation}`, {
          identifiers: input.identifiers,
          params: input.params,
        });
      const form = new FormData();
      form.append('file', input.file);
      form.append('params', JSON.stringify(input.params));
      return api.upload<BulkBatchDto>(`/bulk/${operation}/file`, form);
    },
    // A batch changes units, so the report, the dashboard and the model counts are all stale.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bulk'] }),
        queryClient.invalidateQueries({ queryKey: ['inventory'] }),
        queryClient.invalidateQueries({ queryKey: ['product-models'] }),
      ]),
  });
}

/** The second step of a two-step batch ("Send to Disconnect", "Send to Swap SIM", ...). */
export function useBulkSend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) => api.post<BulkBatchDto>(`/bulk/batches/${batchId}/send`, {}),
    // These batches change lines and customers as well as stock.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bulk'] }),
        queryClient.invalidateQueries({ queryKey: ['inventory'] }),
        queryClient.invalidateQueries({ queryKey: ['customers'] }),
      ]),
  });
}

export const addUnitsTemplateUrl = () => api.url('/bulk/add-units/template');
